import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Facebook, Instagram } from "lucide-react";

const schema = z.object({
  nome: z.string().trim().min(2, "Nome obbligatorio").max(100),
  email: z.string().trim().email("Email non valida").max(255),
  telefono: z.string().trim().max(30).optional().or(z.literal("")),
  oggetto: z.string().trim().min(2).max(150),
  messaggio: z.string().trim().min(10, "Messaggio troppo breve").max(2000),
});

const Contatti = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", oggetto: "", messaggio: "" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const d = parsed.data;
    const { error } = await supabase.from("contact_messages").insert({
      nome: d.nome,
      email: d.email,
      oggetto: d.oggetto,
      messaggio: d.messaggio,
      telefono: d.telefono || undefined,
    } as never);
    setLoading(false);
    if (error) {
      toast.error("Errore nell'invio. Riprova.");
      return;
    }
    toast.success("Messaggio inviato! Ti risponderemo al più presto.");
    setForm({ nome: "", email: "", telefono: "", oggetto: "", messaggio: "" });
  };

  return (
    <>
      <SEO
        title="Contatti — FIABA ETS"
        description="Contatta FIABA ETS: Piazzale degli Archivi 41, 00144 Roma. Tel 06 43400800. Email segreteria@fiaba.org."
        path="/contatti"
      />
      <PageHero
        eyebrow="Contatti"
        title="Scrivici"
        subtitle="Siamo a disposizione per informazioni, collaborazioni e proposte di progetto."
      />
      <section className="section-padding">
        <div className="container-narrow grid lg:grid-cols-5 gap-10">
          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-hero text-primary-foreground p-7 rounded-lg shadow-elegant space-y-5">
              <h2 className="font-serif text-2xl font-bold">FIABA ETS</h2>
              <div className="space-y-4 text-sm">
                <div className="flex gap-3"><MapPin size={18} className="text-secondary shrink-0 mt-0.5" /><span>Piazzale degli Archivi, 41<br/>00144 Roma RM</span></div>
                <div className="flex gap-3"><Phone size={18} className="text-secondary shrink-0 mt-0.5" /><span><a href="tel:+390643400800" className="hover:text-secondary block">06 43400800</a><a href="tel:+393297051608" className="hover:text-secondary block">329 7051608</a></span></div>
                <div className="flex gap-3"><Mail size={18} className="text-secondary shrink-0 mt-0.5" /><span><a href="mailto:segreteria@fiaba.org" className="hover:text-secondary block break-all">segreteria@fiaba.org</a><a href="mailto:fiaba@pec.it" className="hover:text-secondary block">fiaba@pec.it</a></span></div>
              </div>
              <div className="flex gap-2 pt-4 border-t border-primary-foreground/20">
                <a href="https://www.facebook.com/fiabaets" aria-label="Facebook" className="p-2 rounded-full bg-primary-foreground/10 hover:bg-secondary hover:text-secondary-foreground transition"><Facebook size={18} /></a>
                <a href="https://www.instagram.com/fiabaets" aria-label="Instagram" className="p-2 rounded-full bg-primary-foreground/10 hover:bg-secondary hover:text-secondary-foreground transition"><Instagram size={18} /></a>
              </div>
            </div>
            <iframe
              title="Mappa sede FIABA"
              src="https://www.google.com/maps?q=Piazzale+degli+Archivi+41+Roma&output=embed"
              className="w-full h-64 rounded-lg shadow-soft border border-border"
              loading="lazy"
            />
          </aside>

          <form onSubmit={onSubmit} className="lg:col-span-3 bg-card p-8 rounded-lg shadow-elegant border border-border space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input id="nome" maxLength={100} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" maxLength={255} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tel">Telefono</Label>
                <Input id="tel" maxLength={30} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="ogg">Oggetto *</Label>
                <Input id="ogg" maxLength={150} value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="msg">Messaggio *</Label>
              <Textarea id="msg" rows={6} maxLength={2000} value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })} />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-hero text-primary-foreground hover:opacity-90 h-12 text-base font-semibold">
              {loading ? "Invio in corso..." : "Invia messaggio"}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Contatti;
